import { ModuleWithProviders, NgModule, Optional, Provider } from '@angular/core';
import { SidebarPortalHostComponent } from './services/sidebar/sidebar-portal-host.component';
import { ApolloService } from './services/apollo.service';
import { EditDialogService } from './components/editDialog/editDialogService';
import { ConfirmDialogService } from './components/confirmDialog/confirmDialogService';
import { ViewDialogService } from './components/viewDialog/viewDialogService';
import { LocationService } from './services/location/location.service';
import { MetricsService } from './services/metrics/metrics.service';
import { UsersService } from './services/users/users.service';
// #735: einmaliger Sprach-Abgleich Backend <-> localStorage-Cache pro Session
import { PreferredLanguageSyncService } from './services/preferredLanguage/preferred-language-sync.service';
import { LoadingService } from './services/loading.service';
import { RolesService } from './services/roles/roles.service';
import { SettingsService } from './services/settings/settings.service';
import { ToastService } from './services/toast/toast.service';
import {
  ADDITIONAL_FLOWS,
  ADDITIONAL_COMPLETION_BEHAVIORS,
  FLOW_MAP,
  COMPLETION_BEHAVIOR_MAP,
  CommandService,
  Flow,
  CompletionBehavior,
} from './services/command/command.service';
import { StateService } from './services/state/state-service';
import { CoreComponentsModule } from './core-components.module';
import { TenantsService } from './services/tenants/tenants.service';
import { ApiKeysService } from './services/apiKeys/apiKeys.service';
import { ConnectedDeviceSettingsService } from './services/connectedDeviceSettings/connected-device-settings.service';
import { GroupsService } from './services/groups/groups.service';
import { TimeTrackingCategoryService } from './services/timeTrackingCategories/timeTrackingCategories.service';
import { PaymentsService } from './services/payment/payment.service';
import { NoFlow } from './services/command/flows/noFlow';
import { ConfirmationDialogFlow } from './services/command/flows/confirmationDialogFlow';
import { RedirectCompletionBehavior } from './services/command/completionBehavior/redirectCompletionBehavior';
import { ReloadCompletionBehavior } from './services/command/completionBehavior/reloadCompletionBehavior';
import { ToastCompletionBehavior } from './services/command/completionBehavior/toastCompletionBehavior';

const localFlowMapProvider = {
  provide: FLOW_MAP,
  useFactory: (
    confirmationDialogFlow: ConfirmationDialogFlow,
    noFlow: NoFlow,
    additionalFlows: Map<string, Flow>[]
  ) => {
    const map = new Map<string, Flow>();
    map.set('confirmationDialogFlow', confirmationDialogFlow);
    map.set('noFlow', noFlow);
    // ADDITIONAL_FLOWS is provided with multi:true → an array of Map<string, Flow>.
    // Iterate every map and merge its entries individually.
    if (additionalFlows) {
      additionalFlows.forEach((flowMap) => {
        flowMap.forEach((flow, key) => map.set(key, flow));
      });
    }
    return map;
  },
  deps: [
    ConfirmationDialogFlow,
    NoFlow,
    [new Optional(), ADDITIONAL_FLOWS],
  ],
};

const localCompletionBehaviorMapProvider = {
  provide: COMPLETION_BEHAVIOR_MAP,
  useFactory: (
    redirectCompletionBehavior: RedirectCompletionBehavior,
    reloadCompletionBehavior: ReloadCompletionBehavior,
    toastCompletionBehavior: ToastCompletionBehavior,
    additionalCompletionBehaviors: Map<string, CompletionBehavior>[]
  ) => {
    const map = new Map<string, CompletionBehavior>();
    map.set('redirectCompletionBehavior', redirectCompletionBehavior);
    map.set('reloadCompletionBehavior', reloadCompletionBehavior);
    map.set('toastCompletionBehavior', toastCompletionBehavior);
    // ADDITIONAL_COMPLETION_BEHAVIORS is provided with multi:true → an array of
    // Map<string, CompletionBehavior>. Iterate every map and merge individually.
    if (additionalCompletionBehaviors) {
      additionalCompletionBehaviors.forEach((behaviorMap) => {
        behaviorMap.forEach((behavior, key) => map.set(key, behavior));
      });
    }
    return map;
  },
  deps: [
    RedirectCompletionBehavior,
    ReloadCompletionBehavior,
    ToastCompletionBehavior,
    [new Optional(), ADDITIONAL_COMPLETION_BEHAVIORS],
  ],
};

@NgModule({
  imports: [SidebarPortalHostComponent, CoreComponentsModule],
  exports: [CoreComponentsModule, SidebarPortalHostComponent],
  providers: [
    ApolloService,
    LoadingService,
    RolesService,
    SettingsService,
    ToastService,
    CommandService,
    LocationService,
    MetricsService,
    UsersService,
    PreferredLanguageSyncService,
    StateService,
    EditDialogService,
    ConfirmDialogService,
    ViewDialogService,
    TenantsService,
    ApiKeysService,
    ConnectedDeviceSettingsService,
    GroupsService,
    TimeTrackingCategoryService,
    PaymentsService,
    localFlowMapProvider,
    localCompletionBehaviorMapProvider,
  ],
})
export class CoreModule {
  static forRoot(environment: any, additionalFlows?: Provider[]): ModuleWithProviders<CoreModule> {
    return {
      ngModule: CoreModule,
      providers: [
        { provide: 'env', useValue: environment },
        ...(additionalFlows || []),
      ],
    };
  }
}
